// Reading an answer as the server may send it, not as the query's types
// promise.
//
// GitHub's GraphQL answers a field it will not show (a token without a
// permission, a deleted account, an object that failed to load) with null
// and an entry in `errors`, and the null travels up to the nearest field
// that may be null. A query's result type says what a whole answer looks
// like; an answer with holes typed as that type is a lie the code finds
// out about wherever it first reads through one, which in a value the
// page derives ends the page's updates.
//
// So an answer is decoded where it is fetched, against a `Shape` that says
// for every field what stands in when it is missing:
//
// - a text, number or flag has a default (`str`, `num`, `flag`);
// - a list is empty, and an item that is null or cannot be read is left
//   out (`list`);
// - a field that may be null is null (`nul`), one that may be absent is
//   absent (`opt`);
// - an object is made of its fields' defaults (`obj`), unless it has a
//   field without which it is nothing (`key`: its ID, a commit's SHA):
//   then the object itself is missing, and is dropped from its list, null
//   where it may be, or makes its parent missing in turn;
// - one of several types by a tag (`union`) is missing when the tag is
//   none of them.
//
// What `decode` returns has every field the shape names in place, so code
// typed against the result type cannot throw on a hole. `MISSING` at the
// top says the answer lacks what it cannot do without. Fields the shape
// does not name pass through as they are.
//
// Decoded so far: the pull request and its pages, its checks and its
// deployments (api/answers.ts). Other queries are still read as typed.

/** What stands for a field: see the file comment. */
export type Shape =
  | { k: 'str'; d: string }
  | { k: 'num'; d: number }
  | { k: 'flag'; d: boolean }
  | { k: 'key' }
  | { k: 'nul'; of: Shape }
  | { k: 'opt'; of: Shape }
  | { k: 'list'; of: Shape }
  | { k: 'obj'; fields: Readonly<Record<string, Shape>> }
  | { k: 'union'; tag: string; variants: Readonly<Record<string, Shape>> };

/** The value is not there, and nothing can stand in for it. */
export const MISSING: unique symbol = Symbol('missing');
export type Missing = typeof MISSING;

export const str = (d = ''): Shape => ({ k: 'str', d });
export const num = (d = 0): Shape => ({ k: 'num', d });
export const flag = (d = false): Shape => ({ k: 'flag', d });
/** A text or number the object is nothing without. */
export const key: Shape = { k: 'key' };
export const nul = (of: Shape): Shape => ({ k: 'nul', of });
export const opt = (of: Shape): Shape => ({ k: 'opt', of });
export const list = (of: Shape): Shape => ({ k: 'list', of });
export const obj = (fields: Record<string, Shape>): Shape => ({ k: 'obj', fields });
export const union = (tag: string, variants: Record<string, Shape>): Shape => ({ k: 'union', tag, variants });
/** A connection: its `nodes`, and what else it carries. */
export const nodes = (of: Shape, more: Record<string, Shape> = {}): Shape => obj({ nodes: list(of), ...more });

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** `value` read as `shape`: every field in place, or MISSING. Never throws. */
export function decode(shape: Shape, value: unknown): unknown | Missing {
  switch (shape.k) {
    case 'str':
      return typeof value === 'string' ? value : shape.d;
    case 'num':
      return typeof value === 'number' && Number.isFinite(value) ? value : shape.d;
    case 'flag':
      return typeof value === 'boolean' ? value : shape.d;
    case 'key':
      return (typeof value === 'string' && value !== '') || (typeof value === 'number' && Number.isFinite(value)) ? value : MISSING;
    case 'nul': {
      if (value === null || value === undefined) return null;
      const v = decode(shape.of, value);
      return v === MISSING ? null : v;
    }
    case 'opt':
      // Handled by the object that holds it (an absent field has no value to return).
      return value === undefined ? MISSING : decode(shape.of, value);
    case 'list':
      // An item that is not there is no item, whatever could stand in for its fields.
      return Array.isArray(value) ? value.filter((v) => v !== null && v !== undefined).map((v) => decode(shape.of, v)).filter((v) => v !== MISSING) : [];
    case 'obj': {
      const from = isObject(value) ? value : {};
      const out: Record<string, unknown> = { ...from };
      for (const [name, field] of Object.entries(shape.fields)) {
        const v = decode(field, from[name]);
        if (v !== MISSING) out[name] = v;
        else if (field.k === 'opt') delete out[name];
        else return MISSING;
      }
      return out;
    }
    case 'union': {
      const tag = isObject(value) ? value[shape.tag] : undefined;
      const variant = typeof tag === 'string' && Object.hasOwn(shape.variants, tag) ? shape.variants[tag] : null;
      return variant ? decode(variant, value) : MISSING;
    }
  }
}
